import { supabase, supabaseAdmin } from '../lib/supabaseClient';

export interface UserSummary {
  id: string;
  fullName: string | null;
  email: string;
}

export interface CustomerUserRecord {
  id: string;
  fullName: string | null;
  email: string;
  phone: string | null;
  createdAt: string | null;
  pendingEmail?: string | null;
  emailChangeSentAt?: string | null;
  verificationRequired?: boolean;
}

export interface UpdateCustomerUserInput {
  userId: string;
  fullName?: string;
  email: string;
  phone?: string;
}

const callAdminCustomers = async <T>(
  action: string,
  payload: Record<string, unknown> = {}
): Promise<T> => {
  const { data, error } = await supabase.functions.invoke('admin-customers', {
    body: { action, payload },
  });

  if (error) {
    throw error;
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  return data?.data as T;
};

export async function searchUsers(query: string): Promise<UserSummary[]> {
  const trimmed = query.trim();
  if (trimmed.length < 2) {
    return [];
  }

  const client = supabaseAdmin ?? supabase;
  const { data, error } = await client
    .from('users')
    .select('id, full_name, email')
    .or(`email.ilike.%${trimmed}%,full_name.ilike.%${trimmed}%`)
    .order('full_name', { ascending: true })
    .limit(10);

  if (error) {
    throw error;
  }

  return (data ?? []).map((row) => ({
    id: row.id,
    fullName: row.full_name ?? null,
    email: row.email,
  }));
}

export async function listCustomerUsers(query = ''): Promise<CustomerUserRecord[]> {
  const trimmed = query.trim();
  const client = supabaseAdmin ?? supabase;

  let usersQuery = client
    .from('users')
    .select('id, full_name, email, phone, created_at')
    .order('created_at', { ascending: false })
    .limit(trimmed ? 100 : 250);

  if (trimmed.length >= 2) {
    usersQuery = usersQuery.or(
      `email.ilike.%${trimmed}%,full_name.ilike.%${trimmed}%,phone.ilike.%${trimmed}%`
    );
  }

  const [{ data: users, error: usersError }, { data: roleRows, error: rolesError }] =
    await Promise.all([
      usersQuery,
      client.from('user_roles').select('user_id'),
    ]);

  if (usersError) {
    throw usersError;
  }

  if (rolesError) {
    throw rolesError;
  }

  const privilegedIds = new Set((roleRows ?? []).map((row) => row.user_id));

  return (users ?? [])
    .filter((row) => !privilegedIds.has(row.id))
    .map((row) => ({
      id: row.id,
      fullName: row.full_name ?? null,
      email: row.email,
      phone: row.phone ?? null,
      createdAt: row.created_at ?? null,
    }));
}

export async function deleteCustomerUser(userId: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('delete-account', {
    body: { targetUserId: userId },
  });

  if (error) {
    throw error;
  }

  if (data?.error) {
    throw new Error(data.error);
  }
}

export async function updateCustomerUser({
  userId,
  fullName,
  email,
  phone,
}: UpdateCustomerUserInput): Promise<CustomerUserRecord> {
  return await callAdminCustomers<CustomerUserRecord>('update_customer_profile', {
    userId,
    fullName,
    email,
    phone,
  });
}
